import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import '../../component-styles/ReportsShared.css';
import '../../component-styles/TimeBillingReport.css';
import API_BASE from '../../utils/api.js';
import { exportRowsToExcel } from '../../utils/excelExport.js';

/* --------------------------------------------------------
   Helpers
-------------------------------------------------------- */
function fmtUSD(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
  });
}

function normalizeProject(raw) {
  return {
    id: String(raw.projectId || raw.id || raw.name || ''),
    name: raw.projectNickname || raw.name || raw.title || 'Untitled Project',
  };
}

function getDefaultDateRange() {
  const year = new Date().getFullYear();
  return { start: `${year}-01-01`, end: `${year}-12-31` };
}

/* ========================================================
   TIME BILLING INVOICE REPORT PAGE
======================================================== */
export default function TimeBillingInvoiceReportPage() {
  const navigate = useNavigate();

  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const userId = localStorage.getItem('userId');
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [projectFilter, setProjectFilter] = useState('');

  useEffect(() => {
    async function loadProjects() {
      if (!userId) {
        setProjects([]);
        return;
      }
      try {
        const res = await fetch(`${API_BASE}/api/projects/${encodeURIComponent(userId)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const list = Array.isArray(data) ? data : data?.Items || data?.items || [];
        setProjects(list.map(normalizeProject));
      } catch (err) {
        console.error('Failed to load projects', err);
        setProjects([]);
      }
    }
    loadProjects();
  }, [userId]);

  /* --------------------------------------------------------
     Fetch invoices
  -------------------------------------------------------- */
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          startDate,
          endDate,
          ...(projectFilter && { projectId: projectFilter }),
        });

        const res = await fetch(`${API_BASE}/api/reports/time-billing-invoices?${params}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('idToken')}`,
          },
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        setRows(Array.isArray(data.rows) ? data.rows : []);
      } catch (err) {
        console.error('Failed to load invoice report', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [startDate, endDate, projectFilter]);

  /* --------------------------------------------------------
     Derived data
  -------------------------------------------------------- */
  const totalAmount = useMemo(() => rows.reduce((t, r) => t + Number(r.amount || 0), 0), [rows]);

  /* ======================================================
     ACTIONS
  ===================================================== */

  /* ---------- CSV ---------- */
  function exportExcel() {
    const headers = ['Project ID', 'Project', 'Date', 'Invoice', 'Tags', 'Amount'];

    const rowsForExport = rows.map((r) => [
      r.projectId || '',
      r.projectName || '',
      r.date || '',
      r.invoiceNumber || '',
      r.tags || '',
      Number(r.amount || 0),
    ]);

    exportRowsToExcel({
      filename: 'time-billing-invoices.xlsx',
      sheetName: 'Time Billing Invoices',
      headers,
      rows: rowsForExport,
    });
  }

  /* ---------- PDF ---------- */
  function exportPDF() {
    const doc = new jsPDF('p', 'pt');

    doc.setFontSize(16);
    doc.text('Time Billing Invoice Report', 40, 40);

    doc.setFontSize(10);
    doc.text(`${startDate} - ${endDate}`, 40, 58);

    autoTable(doc, {
      startY: 80,
      head: [['Project', 'Date', 'Invoice', 'Tags', 'Amount']],
      body: rows.map((r) => [r.projectName, r.date, r.invoiceNumber, r.tags, fmtUSD(r.amount)]),
      styles: {
        fontSize: 9,
        cellPadding: 6,
      },
      headStyles: {
        fillColor: [17, 24, 39], // #111827
        textColor: 255,
      },
      columnStyles: {
        4: { halign: 'right' },
      },
    });

    const finalY = doc.lastAutoTable.finalY + 20;
    doc.setFontSize(11);
    doc.text(`Total: ${fmtUSD(totalAmount)}`, 40, finalY);

    doc.save('time-billing-invoices.pdf');
  }

  if (loading) {
    return (
      <div className="report-shell">
        <div className="report-shell-inner">
          <div className="report-empty">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="report-shell">
      <div className="report-shell-inner">
        <div className="time-report-page fade-in">
          {/* Header */}
          <div className="time-report-header report-header">
            <div className="time-report-title report-title">
              <h1>Time Billing Invoice</h1>
              <div className="muted">
                {startDate} - {endDate}
              </div>
            </div>

            <div className="time-report-controls report-filters">
              <select
                className="report-select"
                value={projectFilter}
                onChange={(e) => setProjectFilter(e.target.value)}
              >
                <option value="">All Projects</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>

              <input
                className="report-input"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <input
                className="report-input"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          {/* Table */}
          <div className="time-report-table-wrap report-table-wrap">
            <table className="time-report-table report-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Date</th>
                  <th>Invoice</th>
                  <th>Tags</th>
                  <th className="right">Amount</th>
                </tr>
              </thead>

              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="empty">
                      No invoices found
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr key={r.tbInvoiceId}>
                      <td>{r.projectName}</td>
                      <td>{r.date}</td>
                      <td>
                        {r.projectId && r.tbInvoiceId ? (
                          <a href={`/projects/${r.projectId}/timebilling-invoices/${r.tbInvoiceId}`}>
                            {r.invoiceNumber}
                          </a>
                        ) : (
                          r.invoiceNumber
                        )}
                      </td>
                      <td>{r.tags}</td>
                      <td className="right">{fmtUSD(r.amount)}</td>
                    </tr>
                  ))
                )}
              </tbody>

              <tfoot>
                <tr>
                  <td colSpan={4} />
                  <td className="right">
                    <strong>{fmtUSD(totalAmount)}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Footer */}
          <div className="time-report-footer">
            <button className="timebillingreport-secondary-btn" onClick={() => navigate('/reports')}>
              Cancel
            </button>

            <button className="timebillingreport-secondary-btn" onClick={exportPDF}>
              Share / PDF
            </button>

            <button className="timebillingreport-primary-btn" onClick={exportExcel}>
              Export Excel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
